import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api, ApiFailure } from "../api/client";
import type { Balance, BalanceTransaction, BalanceTransactionKind } from "../api/types";
import { useResource } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { useSession } from "../store/session";
import { DataTable } from "../components/data-table";
import {
  Badge, Button, Card, ErrorState, Field, Loading, PageHeader, inputClass,
} from "../components/ui";
import { formatDateTime } from "./Calls";
import { money } from "./Dashboard";

// Saldo: cuánto hay, qué lo movió y el aviso de saldo bajo.
//
// # Por qué se muestran las dos cifras
//
// `balance` es lo acreditado y `available` es lo que se puede gastar: la
// diferencia es lo comprometido por las llamadas en curso. Mostrar solo una de
// las dos genera la misma pregunta en los dos sentidos — "tengo saldo y no
// puedo marcar" o "me cobraron sin gastar" —, y la respuesta es siempre esa
// resta.
export function BalancePage() {
  const can = useSession((s) => s.can);
  const canCredit = can("billing.manage");

  const [kind, setKind] = useState<"" | BalanceTransactionKind>("");

  // El saldo y el libro mayor se piden por separado: el saldo es un dato, y el
  // libro crece una fila por reserva y por cobro. Antes iban juntos y sin
  // paginar, así que la vista se quedaba en las primeras cien líneas y las
  // anteriores no había forma de verlas.
  const saldo = useResource<Balance>("/api/v1/balance");
  const balance = saldo.data;

  const libro = usePagedResource<BalanceTransaction>(
    `/api/v1/balance/transactions${kind === "" ? "" : `?kind=${kind}`}`,
  );
  const rows = libro.data ?? [];
  const error = saldo.error ?? libro.error;

  function load() {
    void saldo.reload(true);
    void libro.reload(true);
  }

  const columnas = useMemo<ColumnDef<BalanceTransaction, any>[]>(
    () => [
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Cuándo",
        meta: { label: "Cuándo", width: "11rem" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">{formatDateTime(row.original.created_at)}</span>
        ),
      },
      {
        id: "kind",
        accessorKey: "kind",
        header: "Tipo",
        meta: { label: "Tipo", width: "8rem" },
        cell: ({ row }) => (
          <Badge tone={toneOf(row.original.kind)}>{KIND_LABELS[row.original.kind]}</Badge>
        ),
      },
      {
        id: "amount_micros",
        accessorKey: "amount_micros",
        header: "Importe",
        meta: { label: "Importe", align: "right", width: "8rem" },
        cell: ({ row }) => (
          <span
            className={
              "font-mono text-xs tabular-nums " +
              (row.original.amount_micros < 0 ? "text-rose-300" : "text-emerald-300")
            }
          >
            {row.original.amount_micros > 0 ? "+" : ""}
            {money(row.original.amount)}
          </span>
        ),
      },
      {
        id: "balance_after",
        accessorKey: "balance_after",
        header: "Saldo después",
        enableSorting: false,
        meta: { label: "Saldo después", align: "right", hideOnMobile: true, width: "9rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums text-slate-600">
            {money(row.original.balance_after)}
          </span>
        ),
      },
      {
        id: "concepto",
        accessorFn: (tx) => `${tx.reason ?? ""} ${tx.resource_id ?? ""}`,
        header: "Concepto",
        enableSorting: false,
        meta: { label: "Concepto" },
        cell: ({ row }) => (
          <div className="text-xs text-slate-600">
            {row.original.reason || "—"}
            {row.original.resource_id && (
              <div className="font-mono text-[11px] text-slate-600">
                {row.original.resource_type} {row.original.resource_id}
              </div>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  if (balance === null && saldo.error === null) return <Loading />;
  if (balance === null) return <ErrorState message={error ?? ""} onRetry={() => load()} />;

  const low =
    balance.low_balance_threshold_micros !== undefined &&
    balance.available_micros <= balance.low_balance_threshold_micros;

  return (
    <div>
      <PageHeader title="Saldo" description="Prepago: se reserva al marcar y se cobra al terminar." />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Disponible
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-100">
            {money(balance.available)}
          </p>
          <p className="mt-1 text-xs text-slate-500">{balance.currency}</p>
          {low && (
            <p className="mt-2">
              <Badge tone="bad">Saldo bajo</Badge>
            </p>
          )}
        </Card>
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Acreditado
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-100">
            {money(balance.balance)}
          </p>
          <p className="mt-1 text-xs text-slate-500">todo lo que entró menos lo cobrado</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Comprometido
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-100">
            {money(balance.reserved)}
          </p>
          <p className="mt-1 text-xs text-slate-500">reservado por llamadas en curso</p>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <ThresholdForm balance={balance} onSaved={() => void load()} />
        {canCredit && <CreditForm onSaved={() => void load()} />}
      </div>

      <section className="mt-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-slate-100">Movimientos</h2>
          <select
            className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-slate-200 transition focus:border-sky-400/60 focus:outline-none [&>option]:bg-[#11162a]"
            value={kind}
            onChange={(e) => setKind(e.target.value as "" | BalanceTransactionKind)}
          >
            <option value="">Todos</option>
            <option value="credit">Abonos</option>
            <option value="charge">Cargos</option>
            <option value="reservation">Reservas</option>
            <option value="reservation_release">Liberaciones</option>
            <option value="adjustment">Ajustes</option>
          </select>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          El libro es append-only: un movimiento nunca se modifica. Corregir se hace con un
          ajuste, que deja las dos filas y por tanto la explicación.
        </p>

        <DataTable<BalanceTransaction>
          data={rows}
          columns={columnas}
          getRowId={(tx) => tx.id}
          loading={libro.loading}
          error={libro.error}
          onRetry={() => void libro.reload(false)}
          caption="Movimientos de saldo"
          empty={{ title: "Sin movimientos", description: "Todavía no hay nada que mostrar." }}
          search={{ placeholder: "Buscar por concepto o recurso…" }}
          hideableColumns
          urlStateKey="movimientos"
          pagination={{
            pageSize: libro.enEstaPagina,
            hasNext: libro.hayMas,
            hasPrevious: libro.hayAnterior,
            onNext: libro.siguiente,
            onPrevious: libro.anterior,
          }}
        />
      </section>
    </div>
  );
}

const KIND_LABELS: Record<BalanceTransactionKind, string> = {
  credit: "Abono",
  charge: "Cargo",
  reservation: "Reserva",
  reservation_release: "Liberación",
  adjustment: "Ajuste",
};

function toneOf(kind: BalanceTransactionKind): "neutral" | "good" | "warn" | "bad" {
  if (kind === "credit") return "good";
  if (kind === "charge") return "bad";
  if (kind === "adjustment") return "warn";
  return "neutral";
}

/**
 * ThresholdForm configura el umbral del aviso `balance.low`.
 *
 * Se pide en pesos y se manda en micro-MXN: pedirle a una persona que escriba
 * `150000000` para decir 150 pesos es invitar a un error de tres ceros.
 */
function ThresholdForm({ balance, onSaved }: { balance: Balance; onSaved: () => void }) {
  const [value, setValue] = useState(
    balance.low_balance_threshold_micros === undefined
      ? ""
      : String(balance.low_balance_threshold_micros / 1_000_000),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const trimmed = value.trim();
      const micros = trimmed === "" ? null : Math.round(Number(trimmed) * 1_000_000);
      if (micros !== null && (Number.isNaN(micros) || micros < 0)) {
        setError("Escribe un importe en pesos, o deja el campo vacío para apagar el aviso.");
        return;
      }
      await api.put("/api/v1/balance/threshold", { threshold_micros: micros });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-4">
      <h2 className="text-sm font-medium text-slate-100">Aviso de saldo bajo</h2>
      <p className="mt-1 text-xs text-slate-500">
        Al cruzar este importe se emite el webhook <code>balance.low</code>. Vacío apaga el
        aviso.
      </p>
      <form className="mt-3 flex items-end gap-2" onSubmit={(e) => void submit(e)}>
        <Field label={`Umbral en ${balance.currency}`}>
          <input
            className={inputClass}
            inputMode="decimal"
            placeholder="150"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        <Button type="submit" disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </form>
      {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
    </Card>
  );
}

/**
 * CreditForm acredita saldo. Solo con `billing.manage`.
 *
 * Exige motivo porque el backend lo exige, y el backend lo exige porque es
 * dinero que entra sin pasarela de pago: la trazabilidad es lo único que lo
 * hace defendible.
 */
function CreditForm({ onSaved }: { onSaved: () => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(null);

    const micros = Math.round(Number(amount.trim()) * 1_000_000);
    if (!Number.isFinite(micros) || micros <= 0) {
      setError("Escribe un importe positivo en pesos.");
      return;
    }
    if (reason.trim().length < 3) {
      setError("El motivo es obligatorio y queda en el registro de auditoría.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/api/v1/balance/credits", {
        amount_micros: micros,
        reason: reason.trim(),
      });
      setAmount("");
      setReason("");
      setDone("Saldo acreditado.");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo acreditar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="border-white/15 p-4">
      <h2 className="text-sm font-medium text-slate-100">Acreditar saldo</h2>
      <p className="mt-1 text-xs text-slate-500">
        Se acredita a la <strong>cuenta activa</strong>. Queda en el registro de auditoría
        con tu usuario y el motivo.
      </p>
      <form className="mt-3 space-y-3" onSubmit={(e) => void submit(e)}>
        <Field label="Importe en MXN">
          <input
            className={inputClass}
            inputMode="decimal"
            placeholder="1000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Motivo">
          <input
            className={inputClass}
            placeholder="depósito de referencia 4821"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Button type="submit" disabled={saving}>
          {saving ? "Acreditando…" : "Acreditar"}
        </Button>
      </form>
      {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
      {done && <p className="mt-2 text-xs text-emerald-300">{done}</p>}
    </Card>
  );
}
